import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Language } from '../i18n';

export type Theme = 'system' | 'light' | 'dark';

/** Sections of the settings dialog. */
export type SettingsTab =
  | 'general'
  | 'account'
  | 'notifications'
  | 'ai'
  | 'templates'
  | 'plugins'
  | 'mcp'
  | 'management'
  | 'shortcuts'
  | 'about';

export type TasksTab =
  'today' | 'week' | 'overdue' | 'inbox' | 'projects' | 'board' | 'calendar' | 'all';

/** A tab shows a page, or one of the built-in views. */
export type View =
  | { kind: 'page'; pageId: string }
  | { kind: 'tag'; tag: string }
  | { kind: 'search'; query: string }
  | { kind: 'tasks'; tab: TasksTab }
  | { kind: 'projects' }
  | { kind: 'dashboard' }
  /** The graph; with `center` the pages around that page. */
  | { kind: 'graph'; center?: string | null }
  | { kind: 'import' }
  | { kind: 'nexus-import' }
  /** The template module (ADR 0024). */
  | { kind: 'templates' }
  /** Onthouden: things to remember, with or without a reminder (ADR 0027). */
  | { kind: 'memos' }
  /** Tips & tricks. */
  | { kind: 'tips' }
  /** Plugins: the overview, or one plugin's own view (ADR 0025). */
  | { kind: 'plugins' }
  | { kind: 'plugin'; pluginId: string; viewId: string }
  | { kind: 'trash' };

/** Identity of a tab. Search and tasks are single tabs: their key ignores the query/sub-view. */
export const viewKey = (v: View) =>
  v.kind === 'page'
    ? `page:${v.pageId}`
    : v.kind === 'tag'
      ? `tag:${v.tag}`
      : v.kind === 'plugin'
        ? `plugin:${v.pluginId}:${v.viewId}`
        : v.kind;

interface UiState {
  tabs: View[];
  active: number;
  sidebarOpen: boolean;
  /** Phones: the sidebar as a drawer over the page (never remembered). */
  drawerOpen: boolean;
  expanded: Record<string, boolean>;
  paletteOpen: boolean;
  /** 'all' = commands + pages (Ctrl+K), 'pages' = quick switcher (Ctrl+O), 'templates'. */
  paletteMode: 'all' | 'pages' | 'templates';
  captureOpen: boolean;
  notificationsOpen: boolean;
  /** A task to scroll to once its page is shown (from a notification). */
  focusTask: { pageId: string; taskId: string } | null;
  settingsOpen: boolean;
  /** Which section the settings show (not remembered). */
  settingsTab: SettingsTab;
  /** A page shown in a pop-up ("peek") over the current view, not remembered. */
  peekPageId: string | null;
  /** The AI assistant panel (Ctrl+J). */
  aiOpen: boolean;
  /** The "Werkruimte herindelen" dialog. */
  reorganizeOpen: boolean;
  /** The page being moved with "Verplaatsen naar" (dialog open while set). */
  moveTarget: string | null;
  /** Task views: subtasks as separate rows instead of grouped under their task. */
  subtasksSeparate: boolean;
  /** Recently visited pages, newest first (this device). */
  recent: string[];
  visit: (pageId: string) => void;
  forgetRecent: (pageId: string) => void;
  /** Collapse every folder in the page tree. */
  collapseAll: () => void;
  theme: Theme;
  language: Language;
  /** Default for pages without their own choice. */
  fullWidthDefault: boolean;
  /** Per-page full-width choice (this device). */
  pageWidth: Record<string, boolean>;
  setPageWidth: (pageId: string, fullWidth: boolean) => void;
  /** Open a view; reuse the active tab unless `newTab` or it is already open elsewhere. */
  open: (view: View, newTab?: boolean) => void;
  closeTab: (index: number) => void;
  focusTab: (index: number) => void;
  /** Drop tabs whose page no longer exists (trashed/deleted). */
  pruneTabs: (livePageIds: Set<string>) => void;
  toggleExpanded: (id: string, value?: boolean) => void;
  set: (
    patch: Partial<
      Pick<
        UiState,
        | 'sidebarOpen'
        | 'drawerOpen'
        | 'paletteOpen'
        | 'paletteMode'
        | 'captureOpen'
        | 'notificationsOpen'
        | 'focusTask'
        | 'settingsOpen'
        | 'settingsTab'
        | 'aiOpen'
        | 'reorganizeOpen'
        | 'moveTarget'
        | 'subtasksSeparate'
        | 'theme'
        | 'peekPageId'
        | 'language'
        | 'fullWidthDefault'
      >
    >,
  ) => void;
}

// localStorage only holds per-device conveniences; it may be unavailable (private mode).
const safeStorage = createJSONStorage(() => {
  try {
    const k = '__nexus_probe__';
    localStorage.setItem(k, k);
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k) => mem.get(k) ?? null,
      setItem: (k, v) => void mem.set(k, v),
      removeItem: (k) => void mem.delete(k),
    };
  }
});

export const useUi = create<UiState>()(
  persist(
    (set, get) => ({
      tabs: [],
      active: -1,
      sidebarOpen: true,
      drawerOpen: false,
      expanded: {},
      paletteOpen: false,
      paletteMode: 'all',
      captureOpen: false,
      notificationsOpen: false,
      focusTask: null,
      settingsOpen: false,
      settingsTab: 'general',
      peekPageId: null,
      aiOpen: false,
      reorganizeOpen: false,
      moveTarget: null,
      subtasksSeparate: false,
      recent: [],
      theme: 'system',
      language: 'nl',
      fullWidthDefault: false,
      pageWidth: {},
      setPageWidth: (pageId, fullWidth) =>
        set((s) => ({ pageWidth: { ...s.pageWidth, [pageId]: fullWidth } })),
      open: (view, newTab = false) => {
        const { tabs, active } = get();
        const existing = tabs.findIndex((t) => viewKey(t) === viewKey(view));
        if (existing !== -1) {
          // Single tabs (search, tasks) take the new query or sub-view.
          const next =
            view.kind === 'search' || view.kind === 'tasks' || view.kind === 'graph'
              ? tabs.map((t, i) => (i === existing ? view : t))
              : tabs;
          return set({ tabs: next, active: existing });
        }
        if (newTab || active === -1) return set({ tabs: [...tabs, view], active: tabs.length });
        const next = [...tabs];
        next[active] = view;
        set({ tabs: next });
      },
      closeTab: (index) => {
        const { tabs, active } = get();
        const next = tabs.filter((_, i) => i !== index);
        const nextActive = index < active ? active - 1 : Math.min(active, next.length - 1);
        set({ tabs: next, active: nextActive });
      },
      focusTab: (index) => set({ active: index }),
      pruneTabs: (live) => {
        const { tabs, active } = get();
        const current = tabs[active];
        const next = tabs.filter((t) => t.kind !== 'page' || live.has(t.pageId));
        if (next.length === tabs.length) return;
        const idx = current ? next.findIndex((t) => viewKey(t) === viewKey(current)) : -1;
        set({ tabs: next, active: idx !== -1 ? idx : next.length - 1 });
      },
      toggleExpanded: (id, value) =>
        set((s) => ({ expanded: { ...s.expanded, [id]: value ?? !s.expanded[id] } })),
      collapseAll: () => set({ expanded: {} }),
      visit: (pageId) =>
        set((s) =>
          s.recent[0] === pageId
            ? s
            : { recent: [pageId, ...s.recent.filter((id) => id !== pageId)].slice(0, 15) },
        ),
      forgetRecent: (pageId) => set((s) => ({ recent: s.recent.filter((id) => id !== pageId) })),
      set: (patch) => set(patch),
    }),
    {
      name: 'nexus-ui',
      storage: safeStorage,
      partialize: (s) => ({
        tabs: s.tabs,
        active: s.active,
        sidebarOpen: s.sidebarOpen,
        expanded: s.expanded,
        theme: s.theme,
        language: s.language,
        fullWidthDefault: s.fullWidthDefault,
        pageWidth: s.pageWidth,
        aiOpen: s.aiOpen,
        recent: s.recent,
        subtasksSeparate: s.subtasksSeparate,
      }),
    },
  ),
);
